import { Banner, BannerAction, BannerItem } from '@/ui/feedback/Banner';
import { toast } from '@/ui/overlays/toast';
import { FREE, TRIAL } from 'Types/plan';
import { CircleAlert, CreditCard, Info, MailWarning } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSetUpBilling } from 'App/components/Client/Billing/useSetUpBilling';
import { useStore } from 'App/mstore';

// these stop the account, so they stay until the reason is gone
const DANGER_LEVELS = ['alert', 'error'];
// the free plan's monthly recordings, until /plans says otherwise
const FREE_ALLOWANCE = 1_000;

/** SaaS: the strips above the app. An announcement from `account.alerts` comes
 *  first; without one, the first user's setup (verify the email, set up billing). */
function AppBanners() {
  const { t } = useTranslation();
  const { userStore, billingStore } = useStore();
  const { account } = userStore;
  const setUpBilling = useSetUpBilling();
  // dismissed for this visit
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [setupClosed, setSetupClosed] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  if (!account?.id) return null;

  const alerts = (account.alerts ?? [])
    .map((alert) => ({
      ...alert,
      key: `${alert.level}:${alert.text}`,
      danger: DANGER_LEVELS.includes(alert.level?.toLowerCase() ?? ''),
    }))
    .filter((alert) => alert.danger || !dismissed.includes(alert.key));

  if (alerts.length) {
    return (
      <>
        {alerts.map((alert) => (
          <Banner
            key={alert.key}
            tone={alert.danger ? 'danger' : 'info'}
            label={t('Announcement')}
            icon={alert.danger ? <CircleAlert size={14} /> : <Info size={14} />}
            onDismiss={
              alert.danger
                ? undefined
                : () => setDismissed((keys) => [...keys, alert.key])
            }
          >
            <BannerItem
              action={
                alert.button && alert.url ? (
                  <BannerAction href={alert.url}>{alert.button}</BannerAction>
                ) : undefined
              }
            >
              {alert.text}
            </BannerItem>
          </Banner>
        ))}
      </>
    );
  }

  const { email } = account;
  const resend = () =>
    userStore
      .resendEmailVerification(email)
      .then(() => {
        toast.success(t('Verification email sent to {{email}}', { email }));
        setVerificationSent(true);
      })
      .catch(() => toast.error(t('Could not send the verification email')));

  const plan = account.plan;
  // the fetched plan is fresher than the account's after Start plan
  const type = billingStore.planFetched
    ? billingStore.instance.current.type
    : plan?.type;
  const needsBilling =
    !billingStore.instance.hasActivePlan &&
    (type === TRIAL || (type === FREE && !plan?.billingSetup));
  const trialDays = plan?.remainingTrialDays ?? 0;
  const allowance =
    billingStore.instance.current.recordedSessions || FREE_ALLOWANCE;

  if (setupClosed || (account.verifiedEmail && !needsBilling)) return null;
  return (
    <Banner
      tone="neutral"
      label={t('Finish setting up')}
      onDismiss={() => setSetupClosed(true)}
    >
      {account.verifiedEmail ? null : (
        <BannerItem
          icon={<MailWarning size={14} />}
          action={
            verificationSent ? undefined : (
              <BannerAction onClick={() => void resend()}>
                {t('Resend')}
              </BannerAction>
            )
          }
        >
          <strong>{t('Verify your email.')}</strong>{' '}
          {t('The link went to {{email}}.', { email })}
        </BannerItem>
      )}
      {needsBilling ? (
        <BannerItem
          icon={<CreditCard size={14} />}
          action={
            <BannerAction onClick={setUpBilling}>
              {t('Set up billing')}
            </BannerAction>
          }
        >
          <strong>{t('No active plan.')}</strong>{' '}
          {type === TRIAL
            ? trialDays > 0
              ? t('The trial ends in {{n}} days.', {
                  n: trialDays,
                  count: trialDays,
                })
              : t('The trial has ended.')
            : t('You are on the free {{n}} recordings a month.', {
                n: allowance.toLocaleString(),
              })}
        </BannerItem>
      ) : null}
    </Banner>
  );
}

export default observer(AppBanners);
